import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { Warning, Lightning, X } from "@phosphor-icons/react";

export default function TrialBanner() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const [access, setAccess] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) { setAccess(null); return; }
    api.get("/billing/access").then((r) => setAccess(r.data)).catch(() => setAccess(null));
  }, [user]);

  if (!user || !access || dismissed) return null;
  if (location.pathname.startsWith("/billing")) return null;
  if (location.pathname.startsWith("/sign/") || location.pathname.startsWith("/pay/") || location.pathname.startsWith("/parte/")) return null;
  if (access.reason === "active" || access.reason === "trialing") return null;

  const T = (es, en) => (lang === "es" ? es : en);
  const expired = access.reason === "expired";
  const daysLeft = access.trial_days_left;

  const color = expired ? "bg-red-600" : daysLeft <= 3 ? "bg-amber-500" : "bg-blueprint";
  const Icon = expired ? Warning : Lightning;

  const useDarkText = !expired && daysLeft <= 3;
  const textClass = useDarkText ? "text-ink" : "text-white";
  const dismissClass = useDarkText ? "text-ink/80 hover:text-ink" : "text-white/80 hover:text-white";

  return (
    <div className={`${color} ${textClass} px-4 py-2 text-sm flex items-center gap-3 justify-center`}
      data-testid="trial-banner">
      <Icon size={16} weight="bold" />
      <div className="font-medium">
        {expired
          ? T("Tu período de prueba ha terminado. Suscríbete desde 19€/mes para seguir gestionando obras.",
               "Your trial has ended. Subscribe from €19/month to keep managing your works.")
          : daysLeft === 0
            ? T("Tu prueba gratuita termina hoy. Activa tu plan desde 19€/mes.", "Your free trial ends today. Activate your plan from €19/month.")
            : T(`Te quedan ${daysLeft} ${daysLeft === 1 ? "día" : "días"} de prueba gratuita. Planes desde 19€/mes sin permanencia.`,
                 `${daysLeft} ${daysLeft === 1 ? "day" : "days"} left on your free trial. Plans from €19/month with no commitment.`)}
      </div>
      <button data-testid="banner-subscribe" onClick={() => navigate("/billing")}
        className="bg-ink text-white font-bold uppercase tracking-wider px-3 py-1 text-xs shrink-0">
        {expired ? T("Activar ahora", "Activate now") : T("Ver planes", "See plans")}
      </button>
      {!expired && (
        <button data-testid="banner-dismiss" onClick={() => setDismissed(true)}
          aria-label={T("Cerrar aviso de prueba", "Dismiss trial notice")}
          className={dismissClass}>
          <X size={14} weight="bold" />
        </button>
      )}
    </div>
  );
}
